import { lazy } from 'react';

// sessionStorage key guarding the one-time reload, so a genuinely broken
// chunk (not just a stale one) can't put us in an infinite reload loop.
const RELOAD_FLAG = 'chunk-reload-attempted';

// A dynamic import() rejects like this when the hashed chunk it points at is
// gone from the host — which is exactly what happens to a tab that was left
// idle across a redeploy. Match the messages every engine uses.
export const isChunkLoadError = (error) => {
  const message = error?.message || '';
  return (
    error?.name === 'ChunkLoadError' ||
    /Loading chunk [\d]+ failed/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /'text\/html' is not a valid JavaScript MIME type/i.test(message)
  );
};

// Reload once to pull the fresh index.html (and therefore the fresh chunk
// hashes). The sessionStorage guard clears on a successful render, so repeated
// navigations don't keep reloading.
export const reloadForFreshChunks = () => {
  if (sessionStorage.getItem(RELOAD_FLAG)) return false;
  sessionStorage.setItem(RELOAD_FLAG, '1');
  window.location.reload();
  return true;
};

export const clearChunkReloadFlag = () => {
  sessionStorage.removeItem(RELOAD_FLAG);
};

// Drop-in replacement for React.lazy that retries the import once before
// giving up. Handles the transient case (network blip) itself; the persistent
// stale-chunk case falls through to reloadForFreshChunks / the error boundary.
export const lazyWithRetry = (importFn) =>
  lazy(async () => {
    try {
      const module = await importFn();
      clearChunkReloadFlag();
      return module;
    } catch (error) {
      if (isChunkLoadError(error)) {
        // Retry once — covers a transient fetch failure without a full reload.
        try {
          const module = await importFn();
          clearChunkReloadFlag();
          return module;
        } catch (retryError) {
          if (reloadForFreshChunks()) {
            // Return a never-resolving promise so Suspense keeps showing the
            // fallback while the reload takes over the page.
            return new Promise(() => {});
          }
          throw retryError;
        }
      }
      throw error;
    }
  });
